import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DayPickerProps } from "react-day-picker";

import { buttonVariants } from "src/components/ui/button";
import { cn } from "src/lib/utils";

export type CalendarProps = DayPickerProps;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn("tw-p-1", className)}
			classNames={{
				months: "tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row tw-space-y-4 sm:tw-space-x-4 sm:tw-space-y-0",
				month: "tw-space-y-4",
				caption: "tw-flex tw-justify-center tw-pt-1 tw-relative tw-items-center",
				caption_label: "tw-text-[1rem] tw-font-medium",
				nav: "tw-space-x-1 tw-flex tw-items-center",
				nav_button: cn(
					buttonVariants({ variant: "outline" }),
					"[&>svg]:tw-h-6 [&>svg]:tw-w-6 [&>svg]:tw-text-black tw-bg-transparent !tw-p-2 tw-opacity-50 hover:tw-opacity-100"
				),
				nav_button_previous: "tw-absolute tw-left-1",
				nav_button_next: "tw-absolute tw-right-1",
				table: "tw-w-full tw-border-collapse tw-space-y-1",
				head_row: "tw-flex tw-w-full tw-gap-1",
				head_cell: "tw-text-muted-foreground tw-rounded-md tw-font-normal tw-text-sm tw-flex-1",
				row: "tw-flex tw-w-full tw-mt-1 tw-gap-1",
				cell: cn(
					"tw-relative tw-p-0 tw-text-center tw-text-sm focus-within:tw-relative tw-border tw-border-[#ECEFF6] tw-rounded-md focus-within:tw-z-20 [&:has([aria-selected])]:tw-bg-accent [&:has([aria-selected].day-outside)]:tw-bg-accent/50 [&:has([aria-selected].day-range-end)]:tw-rounded-r-md",
					props.mode === "range"
						? "[&:has(>.day-range-end)]:tw-rounded-r-md [&:has(>.day-range-start)]:tw-rounded-l-md first:[&:has([aria-selected])]:tw-rounded-l-md last:[&:has([aria-selected])]:tw-rounded-r-md"
						: "[&:has([aria-selected])]:tw-rounded-md"
				),
				day: cn(buttonVariants({ variant: "ghost" }), "tw-h-5 tw-w-5 tw-p-0 tw-font-normal aria-selected:tw-opacity-100"),
				day_range_start: "day-range-start tw-border tw-border-primary tw-text-white !tw-bg-primary",
				day_range_end: "day-range-end tw-border tw-border-primary tw-text-white !tw-bg-primary",
				day_selected: "hover:tw-bg-primary hover:tw-text-primary-foreground focus:tw-bg-primary focus:tw-text-primary-foreground",
				day_today: "tw-bg-accent tw-text-accent-foreground",
				day_outside: "day-outside tw-text-muted-foreground aria-selected:tw-bg-accent/50 aria-selected:tw-text-muted-foreground",
				day_disabled: "tw-text-muted-foreground tw-opacity-50",
				day_range_middle: "aria-selected:tw-bg-[#F4F7FF] aria-selected:tw-text-accent-foreground",
				day_hidden: "tw-invisible",
				...classNames,
			}}
			components={{
				IconLeft: ({ className, ...iconProps }) => <ChevronLeft className={cn("tw-h-4 tw-w-4", className)} {...iconProps} />,
				IconRight: ({ className, ...iconProps }) => <ChevronRight className={cn("tw-h-4 tw-w-4", className)} {...iconProps} />,
			}}
			{...props}
		/>
	);
}
Calendar.displayName = "Calendar";

export { Calendar };
